import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import cx from 'classnames';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeElementStyle,
} from '@stripe/stripe-js';
import { useNumericFormat } from 'react-number-format';
import { COMMON_CONTAINER_ID } from '@/config';
import { getGoogleAnalyticsIds } from '@/utils';
import { InvoiceRequest } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentSelectors, userSelectors } from '@/redux/selectors';
import { paymentActions } from '@/redux/actions';
import { Button } from '@/components';

import style from './CardCheckoutForm.module.sass';

type CheckoutFormProps = {
  planId: number;
  amount: number;
  onSuccess?: () => void;
};

type CardFieldName = 'number' | 'expiry' | 'cvc';

type CardFieldState = {
  complete: boolean;
  error: string | null;
};

type CardFieldChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent;

const INITIAL_FIELD_STATE: CardFieldState = { complete: false, error: null };

const INITIAL_FIELDS: Record<CardFieldName, CardFieldState> = {
  number: INITIAL_FIELD_STATE,
  expiry: INITIAL_FIELD_STATE,
  cvc: INITIAL_FIELD_STATE,
};

const ELEMENT_STYLE: StripeElementStyle = {
  base: {
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    fontSize: '16px',
    fontWeight: '500',
    color: '#131313',
    '::placeholder': {
      color: '#A1A3A7',
    },
  },
  invalid: {
    color: '#ED274B',
  },
};

const CardCheckoutForm = ({ planId, amount, onSuccess }: CheckoutFormProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { stripeKey, paymentLoading, paymentPlansError } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);

  const [fields, setFields] =
    useState<Record<CardFieldName, CardFieldState>>(INITIAL_FIELDS);
  const [focusedField, setFocusedField] = useState<CardFieldName | null>(null);
  const submittedRef = useRef(false);
  const confirmingRef = useRef(false);

  const numberFormat = useNumericFormat({
    thousandSeparator: ',',
    decimalScale: 2,
  });

  const formattedAmount = numberFormat.format?.(amount.toString()) ?? amount;

  const isFormComplete = useMemo(
    () => Object.values(fields).every((field) => field.complete),
    [fields],
  );

  useEffect(() => {
    if (paymentPlansError) {
      toast.error(paymentPlansError, { containerId: COMMON_CONTAINER_ID });
    }
    return () => {
      dispatch(paymentActions.setError(null));
    };
  }, [paymentPlansError]);

  useEffect(() => {
    dispatch(paymentActions.setStripeKey(null));
    dispatch(paymentActions.setPaymentLoading(false));

    return () => {
      dispatch(paymentActions.setStripeKey(null));
      dispatch(paymentActions.setPaymentLoading(false));
    };
  }, []);

  useEffect(() => {
    if (!paymentLoading && !confirmingRef.current) {
      submittedRef.current = false;
    }
  }, [paymentLoading]);

  useEffect(() => {
    if (!stripeKey || !stripe || !elements) return;

    if (!submittedRef.current) {
      dispatch(paymentActions.setStripeKey(null));
      return;
    }

    if (confirmingRef.current) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) return;

    confirmingRef.current = true;

    const doConfirm = async () => {
      try {
        const { error } = await stripe.confirmCardPayment(stripeKey, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: userData?.fullName,
              email: userData?.emailAddress,
            },
          },
        });

        if (error) {
          toast.error(`${error.message} ${t('translation.try_again_msg')}`, {
            containerId: COMMON_CONTAINER_ID,
          });
        } else {
          toast.success(t('translation.card_deposit_success'), {
            containerId: COMMON_CONTAINER_ID,
          });
          elements.getElement(CardNumberElement)?.clear();
          elements.getElement(CardExpiryElement)?.clear();
          elements.getElement(CardCvcElement)?.clear();
          setFields(INITIAL_FIELDS);
          onSuccess?.();
        }
      } finally {
        confirmingRef.current = false;
        submittedRef.current = false;
        dispatch(paymentActions.setPaymentLoading(false));
        dispatch(paymentActions.setStripeKey(null));
      }
    };

    doConfirm();
  }, [stripeKey, stripe, elements]);

  const handleFieldChange = useCallback(
    (name: CardFieldName) => (event: CardFieldChangeEvent) => {
      setFields((prev) => ({
        ...prev,
        [name]: {
          complete: event.complete,
          error: event.error?.message ?? null,
        },
      }));
    },
    [],
  );

  const performSubmit = async () => {
    if (!stripe || !elements || !isFormComplete || paymentLoading) return;
    if (submittedRef.current) return;

    submittedRef.current = true;
    dispatch(paymentActions.setPaymentLoading(true));

    const { gClientId, gSessionId } = await getGoogleAnalyticsIds();

    const payload: InvoiceRequest = {
      plan: planId,
      asset: 'USD',
      type: 'plan',
      gClientId,
      gSessionId,
    };

    dispatch(paymentActions.createStripeInvoice(payload));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    performSubmit();
  };

  const fieldClasses = (name: CardFieldName) =>
    cx(style.field, {
      [style['is-focused']]: focusedField === name,
      [style['is-invalid']]: !!fields[name].error,
      [style['is-disabled']]: paymentLoading,
    });

  const elementOptions = {
    style: ELEMENT_STYLE,
    disabled: paymentLoading,
  };

  return (
    <form className={style.form} onSubmit={handleSubmit}>
      <label className={cx(style.group, style['is-wide'])}>
        <span className={style.label}>
          {t('translation.card_number_label')}
        </span>
        <div className={fieldClasses('number')}>
          <CardNumberElement
            options={{ ...elementOptions, showIcon: true }}
            onChange={handleFieldChange('number')}
            onFocus={() => setFocusedField('number')}
            onBlur={() => setFocusedField(null)}
          />
        </div>
        {fields.number.error && (
          <span className={style.error}>{fields.number.error}</span>
        )}
      </label>

      <label className={style.group}>
        <span className={style.label}>
          {t('translation.card_expiry_label')}
        </span>
        <div className={fieldClasses('expiry')}>
          <CardExpiryElement
            options={elementOptions}
            onChange={handleFieldChange('expiry')}
            onFocus={() => setFocusedField('expiry')}
            onBlur={() => setFocusedField(null)}
          />
        </div>
        {fields.expiry.error && (
          <span className={style.error}>{fields.expiry.error}</span>
        )}
      </label>

      <label className={style.group}>
        <span className={style.label}>{t('translation.card_cvc_label')}</span>
        <div className={fieldClasses('cvc')}>
          <CardCvcElement
            options={elementOptions}
            onChange={handleFieldChange('cvc')}
            onFocus={() => setFocusedField('cvc')}
            onBlur={() => setFocusedField(null)}
          />
        </div>
        {fields.cvc.error && (
          <span className={style.error}>{fields.cvc.error}</span>
        )}
      </label>

      <Button
        isLoading={paymentLoading}
        corner="rect"
        className={cx(style.btn, style['is-wide'])}
        disabled={!stripe || !isFormComplete || paymentLoading}
        onClick={handleSubmit}
      >
        <span className={style.payLabel}>
          <span>{t('translation.pay_btn')}</span>
          <span className={style.payPrice}>${formattedAmount}</span>
        </span>
      </Button>
    </form>
  );
};

export default CardCheckoutForm;
